import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Input,
  Select,
  Textarea,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';

type Audience = 'all' | 'traveler' | 'host' | 'selected';

interface BroadcastResult {
  sent: number;
  failed: number;
  total_tokens: number;
  stale_removed: number;
  by_platform?: Record<string, { sent: number; failed: number }>;
  errors?: string[];
}

interface PastBroadcast {
  id: string;
  title: string;
  body: string;
  audience: Audience;
  outcome: 'sent' | 'partial' | 'failed' | 'empty';
  sent_count: number;
  failed_count: number;
  total_tokens: number;
  first_error: string | null;
  created_at: string;
  sender: { full_name: string | null } | null;
}

const AUDIENCE_LABEL: Record<Audience, string> = { all: 'All users', traveler: 'Travelers', host: 'Hosts', selected: 'Chosen people' };
const OUTCOME: Record<PastBroadcast['outcome'], { label: string; tone: string }> = {
  sent: { label: 'Sent', tone: 'text-green-700 dark:text-green-300' },
  partial: { label: 'Sent, with problems', tone: 'text-amber-700 dark:text-amber-300' },
  failed: { label: 'Failed', tone: 'text-red-700 dark:text-red-300' },
  empty: { label: 'No devices', tone: 'text-app-muted' },
};

export function AdminPushBroadcast() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<Audience>('all');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [history, setHistory] = useState<PastBroadcast[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const loadHistory = useCallback(async () => {
    const { data } = await supabase
      .from('push_broadcasts')
      .select('id, title, body, audience, outcome, sent_count, failed_count, total_tokens, first_error, created_at, sender:profiles!sent_by(full_name)')
      .order('created_at', { ascending: false })
      .limit(50);
    setHistory((data ?? []) as unknown as PastBroadcast[]);
    setHistoryLoaded(true);
  }, []);

  useEffect(() => { void loadHistory(); }, [loadHistory]);

  function reuse(b: PastBroadcast) {
    setTitle(b.title);
    setBody(b.body);
    // A message to chosen people is not re-sent to a group: only its words come back.
    if (b.audience !== 'selected') setAudience(b.audience);
    setConfirmOpen(false);
    setResult(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSend() {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setError('Not authenticated'); setSending(false); return; }

      const url = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${url}/functions/v1/broadcast-push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({ title, body, audience }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to send');
      } else {
        setResult(data as BroadcastResult);
        setTitle('');
        setBody('');
        void loadHistory();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  const canSend = title.trim() && body.trim() && !sending;
  const audienceLabel = audience === 'all' ? 'all users' : audience === 'traveler' ? 'travelers' : 'hosts';

  return (
    <AdminPage title="Push Notifications" description="Send a push notification to all app users (Android & iOS).">
      <div className="max-w-lg space-y-5">
        <Input
          label="Title"
          placeholder="e.g. New feature available!"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={100}
        />

        <Textarea
          label="Message"
          placeholder="Write the notification message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={500}
        />

        <Select
          label="Audience"
          value={audience}
          onChange={(e) => setAudience(e.target.value as Audience)}
          options={[
            { value: 'all', label: 'All users' },
            { value: 'traveler', label: 'Travelers only' },
            { value: 'host', label: 'Hosts only' },
          ]}
        />

        {/* Preview */}
        {(title.trim() || body.trim()) && (
          <div className="border border-app-border rounded-xl p-4 bg-app-card">
            <p className="text-[10px] uppercase tracking-widest text-app-muted font-medium mb-2">Preview</p>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-gold/20 flex items-center justify-center shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gold">
                  <path d="M4.214 3.227a.75.75 0 00-1.156-.956 8.97 8.97 0 00-1.856 3.826.75.75 0 001.466.316 7.47 7.47 0 011.546-3.186zM16.942 2.271a.75.75 0 00-1.157.956 7.47 7.47 0 011.547 3.186.75.75 0 001.466-.316 8.971 8.971 0 00-1.856-3.826zM10 2a6 6 0 00-6 6c0 1.887-.454 3.665-1.257 5.234a.75.75 0 00.515 1.076 32.91 32.91 0 003.256.508 3.5 3.5 0 006.972 0 32.903 32.903 0 003.256-.508.75.75 0 00.515-1.076A11.448 11.448 0 0116 8a6 6 0 00-6-6z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-app-fg">{title || 'Title'}</p>
                <p className="text-sm text-app-muted mt-0.5">{body || 'Message body'}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          {!confirmOpen ? (
            <Button disabled={!canSend} onClick={() => setConfirmOpen(true)}>
              Send to {audienceLabel}
            </Button>
          ) : (
            <>
              <Button disabled={!canSend} onClick={handleSend}>
                {sending ? 'Sending…' : `Confirm — send to ${audienceLabel}`}
              </Button>
              <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            </>
          )}
        </div>

        {error && (
          <div className="rounded-xl border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {result && (() => {
          const clean = result.total_tokens > 0 && result.failed === 0;
          const tone = clean
            ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300';
          const headline = result.total_tokens === 0
            ? 'No registered devices to send to'
            : clean ? 'Sent successfully' : 'Sent, with problems';
          return (
            <div className={`rounded-xl border px-4 py-3 text-sm ${tone}`}>
              <p className="font-medium">{headline}</p>
              <p className="mt-1">
                {result.sent} delivered · {result.failed} failed · {result.total_tokens} total tokens
                {result.stale_removed > 0 && ` · ${result.stale_removed} stale tokens removed`}
              </p>
              {result.by_platform && Object.keys(result.by_platform).length > 0 && (
                <p className="mt-1">
                  {Object.entries(result.by_platform)
                    .map(([name, c]) => `${name}: ${c.sent} delivered, ${c.failed} failed`)
                    .join(' · ')}
                </p>
              )}
              {result.errors && result.errors.length > 0 && (
                <p className="mt-1 break-words font-mono text-xs">{result.errors[0]}</p>
              )}
            </div>
          );
        })()}
      </div>

      <section className="mt-10 max-w-3xl" aria-label="Sent history">
        <h2 className="text-sm font-semibold text-app-fg mb-3">Sent history</h2>
        {historyLoaded && history.length === 0 && (
          <p className="text-sm text-app-muted">Nothing sent yet. Broadcasts you send from now on are listed here.</p>
        )}
        <ul className="space-y-3">
          {history.map((b) => {
            const o = OUTCOME[b.outcome];
            return (
              <li key={b.id} className="border border-app-border rounded-xl p-4 bg-app-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-app-fg break-words">{b.title}</p>
                    <p className="text-sm text-app-muted mt-0.5 break-words whitespace-pre-wrap">{b.body}</p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => reuse(b)} aria-label={`Reuse "${b.title}"`}>Reuse</Button>
                </div>
                <p className="mt-2 text-xs text-app-muted">
                  {new Date(b.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                  {' · '}{AUDIENCE_LABEL[b.audience]}
                  {' · '}by {b.sender?.full_name?.trim() || 'an admin'}
                  {' · '}<span className={o.tone}>{o.label}</span>
                  {b.outcome !== 'empty' && ` · ${b.sent_count} delivered, ${b.failed_count} failed of ${b.total_tokens}`}
                </p>
                {b.first_error && <p className="mt-1 break-words font-mono text-xs text-app-muted">{b.first_error}</p>}
              </li>
            );
          })}
        </ul>
      </section>
    </AdminPage>
  );
}

export default AdminPushBroadcast;
